<template>
  <div
    :style="cssProps"
    :class="{ 'gap-2': !newsletterForm }"
    class="d-flex flex-column justify-center"
    data-testid="content-creator-success-step"
  >
    <!--
      In master view the SuccessStepMasterContent slot already shows the full
      breakdown (successful / failed / not-connected counts) sourced from the
      batch summary, so a single top-level status icon is both redundant and
      misleading (the raw `posts` prop and the summary counts can disagree).
    -->
    <v-icon
      v-if="!isMasterView"
      :color="statusIcon.color"
      class="mx-auto"
      size="80"
    >
      {{ statusIcon.name }}
    </v-icon>
    <slot>
      <div
        v-if="isUpdateForScheduledPost || isScheduledPost"
        class="text-center"
      >
        <div class="schedule-successful">
          <span
            v-if="isScheduledPost"
            class="schedule-successful-title"
          >
            <template v-if="!isMasterView">
              <span v-if="newsletterForm">
                <span v-if="isReschedule">
                  {{ $t('social-media.alert.emails-reschedule-successful') }}
                </span>
                <span v-else>
                  {{ $t('social-media.alert.emails-schedule-successful') }}
                </span>
              </span>
              <span v-else>{{ $t('social-media.alert.schedule-successful.0') }}</span>
            </template>
          </span>
          <span v-else>
            <span v-if="!newsletterForm">
              {{ $t('social-media.alert.update-schedule-successful.0') }}</span
            >
          </span>
          <br /><br />

          <span v-if="newsletterForm">
            <span v-if="!isMasterView">
              {{ $t('social-media.alert.emails-schedule-successful-info.first') }}:
            </span>
            <span
              class="success-step-summary__statistics"
              v-else
            >
              {{ $t('content-creator.newsletter-details.email-scheduled.0') }}
              <b>{{ newsletterStatistics?.totalRecipients }}</b>
              {{ $t('content-creator.newsletter-details.email-scheduled.1') }}
              <b>"{{ newsletterStatistics?.recipientSegment }}"</b>
              {{ $t('content-creator.newsletter-details.email-scheduled.2') }}.<br /><br />
              <span
                class="mr-2"
                v-for="(statistic, index) in [
                  {
                    icon: 'mdi-calendar-month-outline',
                    text: formatDate(new Date(newsletterStatistics?.invitedAt)),
                  },
                  {
                    icon: 'mdi-calendar-clock',
                    text: formatTime(new Date(newsletterStatistics?.invitedAt)),
                  },
                  {
                    icon: 'mdi-office-building-outline',
                    text: `${newsletterStatistics?.totalOffices} ${$t('content-creator.newsletter-details.offices')}`,
                  },
                ]"
                :key="index"
              >
                <v-icon
                  class="success-step-summary__statistics_icons"
                  :icon="statistic.icon"
                  size="small"
                />
                <span>{{ statistic.text }}</span>
              </span>
            </span>
          </span>
          <span v-else>{{ $t('social-media.alert.schedule-successful.1') }}:</span>
          <template v-if="!isMasterView">
            <div
              v-for="(post, index) in posts"
              :key="post.id || index"
              class="d-flex justify-center scheduled-at my-2"
            >
              <div
                v-for="platform in post.platforms || []"
                :key="platform"
                class="ml-1 mr-2"
              >
                <r-img
                  :src="KindTypeIcon[TypeToKind[platform]]"
                  width="30"
                  height="30"
                  cover
                />
              </div>
              <span
                v-if="post.scheduledAt"
                class="ml-1 align-self-center"
              >
                <v-icon
                  size="24"
                  class="mr-1"
                  >mdi-calendar-month</v-icon
                >
                {{ formatDate(post.scheduledAt) }}
                <v-icon
                  size="24"
                  class="ml-2 mr-1"
                  >mdi-clock</v-icon
                >
                {{ formatTime(post.scheduledAt) }}
              </span>
            </div>
            <br />
            <span v-if="newsletterForm"
              >{{ $t('social-media.alert.emails-schedule-successful-info.second') }}
            </span>
            <span v-else>{{ $t('social-media.alert.schedule-successful.2') }}</span>
            {{ ' ' }}
            <a
              class="link"
              @click="handleClick"
            >
              <span v-if="newsletterForm"
                >{{ $t('social-media.alert.emails-schedule-successful-info.third') }}
              </span>
              <span v-else>{{ $t('social-media.alert.schedule-successful.3') }}</span>
            </a>
            {{ ' ' }}
            <span v-if="newsletterForm">
              {{ $t('social-media.alert.emails-schedule-successful-info.forth') }}
            </span>
            <span v-else>{{ $t('social-media.alert.schedule-successful.4') }}</span>
          </template>
        </div>
      </div>
      <div
        v-else
        class="d-flex flex-column gap-1 align-center schedule-successful"
      >
        <div
          v-if="successfulPosts.length > 0 && !isMasterView"
          class="text-center mb-6 post-successful-title"
        >
          <span v-if="newsletterForm">{{ $t('social-media.alert.email-sent-successful') }}</span>
          <span v-else>{{ $t('social-media.alert.post-successful') }}</span
          ><br />
          <router-link :to="'/content-creator?section=history'">
            {{ $t('social-media.alert.post-successful-link') }}
          </router-link>
        </div>

        <template v-if="!newsletterForm">
          <v-btn
            v-for="post in successfulPosts"
            :key="post.externalPostId"
            target="_blank"
            rel="noopener noreferrer"
            :href="post.permalink"
            class="d-flex align-center pa-6"
          >
            <r-img
              :src="getIcon(post.type)"
              width="30"
              height="30"
              class="success-step-platform-icon mr-2 flex-grow-0"
            />
            {{ getName(post.type) }}
            <v-icon
              color="white"
              size="20"
              class="ml-2"
            >
              mdi-open-in-new
            </v-icon>
          </v-btn>
        </template>

        <v-alert
          v-for="post in failedPosts"
          :key="post.type"
          type="error"
          density="compact"
          text
          class="mb-0"
        >
          {{ $t('social-media.share.post-error', { platform: getName(post.type) }) }}
        </v-alert>
      </div>
      <div
        v-if="isEntryPlan && !newsletterForm"
        class="success-step-upselling"
      >
        <v-divider width="60px" />
        <div class="success-step-upselling-text">
          <p class="font-weight-bold mb-0">
            {{ $t('social-media.upselling.title') }}
          </p>
          <p class="text-body-2 mb-0">
            {{ $t('social-media.upselling.description') }}
          </p>
        </div>
        <v-btn
          color="primary"
          size="large"
          :to="{ name: 'Settings', params: { section: 'contract' }, query: { showUpgrade: '1' } }"
          @click="$tracking.event('Content Creator', 'Clicked', 'Upgrade')"
        >
          <v-icon class="mr-2"> mdi-one-up </v-icon>
          {{ $t('products.button-text.upgrade') }}
        </v-btn>
      </div>
      <div
        v-if="newsletterForm && !isScheduledPost"
        :class="{ 'mt-4': isMasterView }"
        class="receive-email-info"
      >
        <template v-if="isMasterView && newsletterStatistics">
          <div class="success-step-summary__statistics">
            {{ $t('content-creator.newsletter-details.email-post.0') }}
            <b>{{ newsletterStatistics.totalRecipients }}</b>
            {{ $t('content-creator.newsletter-details.email-post.1') }}
            <b>"{{ newsletterStatistics.recipientSegment }}"</b>
            {{ $t('content-creator.newsletter-details.email-post.2') }}.<br /><br />
            <span
              v-for="(statistic, index) in [
                { icon: 'mdi-calendar-month-outline', text: formatDate(new Date(), 'dateLong') },
                { icon: 'mdi-calendar-clock', text: $t('labels.now') },
                {
                  icon: 'mdi-office-building-outline',
                  text: `${newsletterStatistics.totalOffices} ${$t('content-creator.newsletter-details.offices')}`,
                },
              ]"
              :key="index"
              class="preview-step-summary__item mr-2"
            >
              <v-icon
                class="success-step-summary__statistics_icons"
                :icon="statistic.icon"
                size="small"
              />
              <span>{{ statistic.text }}</span>
            </span>
          </div>
          <b>* {{ $t('social-media.receive-email-info') }}</b>
        </template>
        <template v-else>
          <div class="success-step-summary__statistics">
            {{
              $t('alerts.acquisition-booster.newsletter-bulk-sending-notice', {
                count: newsletterStatistics?.totalRecipients,
              })
            }}
          </div>
          <div
            v-if="deliveryStatusStats.length"
            class="mt-2"
          >
            <span
              v-for="(statistic, index) in deliveryStatusStats"
              :key="index"
              class="preview-step-summary__item mr-2"
            >
              <v-icon
                class="success-step-summary__statistics_icons"
                :icon="statistic.icon"
                size="small"
              />
              <span>{{ statistic.text }}</span>
            </span>
          </div>
        </template>
      </div>
    </slot>
    <v-card-actions>
      <slot name="actions">
        <v-spacer />

        <v-btn
          :class="{ 'w-full': $vuetify.display.smAndDown && newsletterForm }"
          color="primary"
          variant="text"
          @click="$emit('close')"
        >
          {{ $t('buttons.finish') }}
        </v-btn>
      </slot>
    </v-card-actions>
  </div>
</template>

<script>
import { AnimationEvents } from '@/lib/animation.js'
import bus, { eventNames } from '@/lib/eventBus.js'
import { ProductType as ProductTypes } from '@/lib/productTypes'
import COMPANY_BASE from '@/queries/CompanyBase.gql'

import { KindName, KindTypeIcon, TypeToKind } from '../enums/KindType'
import { ReadableSocialMedia } from '../enums/SocialMedia'
import { formatDate, formatTime } from '../lib/date'

export default {
  props: {
    posts: {
      type: Array,
      default: () => [],
    },
    newsletterStatistics: {
      type: Object,
      default: () => {},
    },
    isScheduledPost: {
      type: Boolean,
      default: false,
    },
    isUpdateForScheduledPost: {
      type: Boolean,
      default: false,
    },
    newsletterForm: {
      type: Boolean,
      default: false,
    },
    isReschedule: {
      type: Boolean,
      default: false,
    },
    isMasterView: {
      type: Boolean,
      default: false,
    },
    newsletterDeliveryDetails: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    deliveryStatusStats() {
      const d = this.newsletterDeliveryDetails
      if (!d) return []
      return [
        d.sent > 0 && {
          icon: 'mdi-check-circle-outline',
          text: this.$t('content-creator.newsletter-details.delivery-summary-sent', { n: d.sent }),
        },
        d.failed > 0 && {
          icon: 'mdi-alert-circle-outline',
          text: this.$t('content-creator.newsletter-details.delivery-summary-failed', {
            n: d.failed,
          }),
        },
        d.pending > 0 && {
          icon: 'mdi-clock-outline',
          text: this.$t('content-creator.newsletter-details.delivery-summary-pending', {
            n: d.pending,
          }),
        },
      ].filter(Boolean)
    },
    isCreatorPage() {
      return this.$route.query.section === 'creator'
    },
    cssProps() {
      return {
        '--primary': this.$vuetify.theme.global?.current?.colors.primary,
      }
    },
    TypeToKind() {
      return TypeToKind
    },
    KindTypeIcon() {
      return KindTypeIcon
    },
    productType() {
      return this.company?.productType
    },
    isEntryPlan() {
      const entryPlans = [ProductTypes.BEGINNER, ProductTypes.LIGHT]
      return this.productType && entryPlans.includes(this.productType)
    },
    successfulPosts() {
      return this.posts.filter(({ hasFailed }) => !hasFailed) || []
    },
    failedPosts() {
      return this.posts.filter(({ hasFailed }) => hasFailed) || []
    },
    statusIcon() {
      // Master view does not render this icon (see template); the summary slot
      // shows the per-status breakdown instead.
      if (this.failedPosts && this.failedPosts.length === 0) {
        return { name: 'mdi-check-circle', color: 'success' }
      }
      if (this.successfulPosts && this.successfulPosts.length > 0) {
        return { name: 'mdi-alert-circle', color: 'warning' }
      }
      return { name: 'mdi-close-circle', color: 'error' }
    },
  },
  mounted() {
    if (this.successfulPosts?.length > 0 && this.failedPosts?.length === 0) {
      bus.$emit(eventNames.TRIGGER_ANIMATION, AnimationEvents.CONTENT_POSTED)
    }
  },
  methods: {
    formatTime,
    formatDate,
    getIcon(type) {
      return KindTypeIcon[TypeToKind[type]]
    },
    getName(type) {
      return ReadableSocialMedia[type] || KindName[TypeToKind[type]]
    },
    handleClick() {
      if (!this.isCreatorPage) {
        this.$emit('close')
        return
      }

      this.$router.push({
        path: '/content-creator',
        query: { section: 'scheduled-posts' },
      })
    },
  },
  emits: ['close'],
  apollo: {
    company: {
      query: COMPANY_BASE,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
      fetchPolicy: 'cache-first',
    },
  },
}
</script>

<style scoped>
.schedule-successful {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}
.post-successful-title,
.schedule-successful-title {
  font-weight: 700;
}
.success-step-upselling {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}
.success-step-upselling-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
}
.receive-email-info {
  font-size: 12px;
}
.success-step-summary__statistics {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  text-align: center;
  vertical-align: middle;
  color: #102a21;
}
.success-step-summary__statistics_icons {
  color: #175e51;
}
.success-step-platform-icon {
  background-color: #fff;
  border-radius: 6px;
  overflow: hidden;
}
.success-step-platform-icon :deep(.v-img__img) {
  object-fit: contain;
}
</style>
